import { CardContent, Skeleton, Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import { MprcCard, MprcContainer, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { GetGeneralKeyTopicsPayload, TrainingIndexTopicDto } from 'generated/CoreApi_v1';
import HtmlUtils from 'lib/HtmlUtils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashHBar } from 'views/dashboard/components/Charts/DashHBar';
import { DashFooterExport } from 'views/dashboard/components/DashFooterExport';
import { TrainingIndexProps } from 'views/dashboard/TrainingIndex/TrainingIndexRoot';


////////////////////////////////////////////////////////////////////////////

interface IProps extends TrainingIndexProps {
    getApi: (data: GetGeneralKeyTopicsPayload) => Promise<AxiosResponse<TrainingIndexTopicDto[]>>;
    exportApi?: (data: GetGeneralKeyTopicsPayload) => Promise<AxiosResponse<any>>;
    onDrillDown?: (topic: TrainingIndexTopicDto) => void;
    subtitle: string;
}

////////////////////////////////////////////////////////////////////////////

export const TrainingIndexKeyTopics: React.FC<IProps> = ({ currentMonth, subtitle, getApi, onDrillDown, exportApi }) => {

    const { t } = useTranslation("dashboard");

    const [viewData, setViewData] = useState<TrainingIndexTopicDto[]>([]);
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);
        getApi({ yearMonth: currentMonth })
            .then(response => setViewData(response.data))
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));

    }, [currentMonth, getApi]);

    if (!loading && !viewError && viewData.length == 0) return false;

    ///
    const handleExportExcel = async () => {
        if (!exportApi) return;
        setDownloading(true);
        return HtmlUtils.downloadBlobFromPromise(
            exportApi({ yearMonth: currentMonth })
                .catch(MprcRequestSnackbarErrorReport)
                .finally(() => setDownloading(false))
        );
    };

    return loading ?
        <Skeleton variant="rectangular" height={300} />
        : viewError ?
            <MprcRequestErrorReport error={viewError} />
            :
            <MprcCard>
                <MprcRequestIndicator type="downloading" visible={downloading} />
                <CardContent>
                    <MprcContainer>
                        <Typography variant="h2">{t("TrainingIndex.IndexByKeyTopicTitle")}</Typography>
                        {subtitle && <Typography variant="caption">{subtitle}</Typography>}
                        <DashHBar
                            isPercentage
                            datasets={[
                                {
                                    data: viewData.map(p => p.trainingIndex),
                                }
                            ]}
                            labels={viewData.map(p => p.name)}
                            height={300}
                            onClickDrillDown={onDrillDown ? (index) => onDrillDown(viewData[index]) : undefined}
                        />
                    </MprcContainer>
                    {exportApi && <DashFooterExport onExportData={handleExportExcel} />}
                </CardContent>
            </MprcCard>
        ;
};
